
import { useState, useEffect } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UserPlan {
  plan_type: string;
  credits_remaining: number;
  monthly_limit: number;
  image_generations_used: number;
  full_name: string;
  email: string;
}

export const useUserPlan = () => {
  const { user } = useSecureAuthContext();
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserPlan = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return;
      }

      // Fetch subscriber data for accurate usage tracking
      const { data: subscriberData, error: subscriberError } = await supabase
        .from('subscribers')
        .select('monthly_usage, plan_type')
        .eq('user_id', user.id)
        .single();

      if (subscriberError && subscriberError.code !== 'PGRST116') {
        console.error('Error fetching subscriber data:', subscriberError);
      }

      if (profileData) {
        // Use subscriber data for usage if available, otherwise fall back to profile
        const actualUsage = subscriberData?.monthly_usage || profileData.image_generations_used || 0;
        const planType = subscriberData?.plan_type || profileData.plan_type || 'free';
        const monthlyLimit = profileData.monthly_limit || 250;
        
        // Calculate remaining credits based on actual usage
        const creditsRemaining = Math.max(0, monthlyLimit - actualUsage);

        setUserPlan({
          plan_type: planType,
          credits_remaining: creditsRemaining,
          monthly_limit: monthlyLimit,
          image_generations_used: actualUsage,
          full_name: profileData.full_name || '',
          email: profileData.email || user?.email || ''
        });
      }
    } catch (error) {
      console.error('Error in fetchUserPlan:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchUserPlan();
    }
  }, [user?.id]);

  const getPlanDisplayName = (planType: string) => {
    switch (planType) {
      case 'free': return 'Free Forever';
      case 'hobby': return 'Hobby';
      case 'creator': return 'Creator'; 
      case 'professional': return 'Professional';
      case 'business': return 'Business';
      case 'pay_as_go': return 'Pay as You Go';
      default: return 'Free Forever';
    }
  };

  const getPlanBadgeColor = (planType: string) => {
    switch (planType) {
      case 'free': return 'bg-green-600';
      case 'hobby': return 'bg-blue-600';
      case 'creator': return 'bg-purple-600';
      case 'professional': return 'bg-orange-600';
      case 'business': return 'bg-red-600';
      case 'pay_as_go': return 'bg-gray-600';
      default: return 'bg-green-600';
    }
  };

  const canAccessFeature = (feature: string) => {
    if (!userPlan) return false;
    const plan = userPlan.plan_type;
    
    switch (feature) {
      case 'hd_quality':
        return ['hobby', 'creator', 'professional', 'business', 'pay_as_go'].includes(plan);
      case 'batch_upload':
        return ['free', 'hobby', 'creator', 'professional', 'business', 'pay_as_go'].includes(plan);
      case 'api_access':
        return ['creator', 'professional', 'business'].includes(plan);
      case 'team_collaboration':
        return ['creator', 'professional', 'business'].includes(plan);
      case 'priority_support':
        return ['creator', 'professional', 'business'].includes(plan);
      case 'watermark_removal':
        return ['hobby', 'creator', 'professional', 'business', 'pay_as_go'].includes(plan);
      case 'commercial_rights':
        return ['hobby', 'creator', 'professional', 'business', 'pay_as_go'].includes(plan);
      case 'ultra_hd':
        return ['professional', 'business'].includes(plan);
      case 'custom_integrations':
        return ['professional', 'business'].includes(plan);
      case 'analytics_dashboard':
        return ['professional', 'business'].includes(plan);
      case 'dedicated_support':
        return ['business'].includes(plan);
      case 'enterprise_api':
        return ['business'].includes(plan);
      default:
        return true;
    }
  };

  const hasCreditsRemaining = () => {
    if (!userPlan) return false;
    if (userPlan.plan_type === 'pay_as_go') return true;
    if (userPlan.monthly_limit === 999999) return true; // Unlimited plans
    return userPlan.credits_remaining > 0;
  };

  const getCreditCost = () => {
    if (!userPlan) return 1;
    if (userPlan.plan_type === 'pay_as_go') {
      // Volume pricing logic
      const usedThisMonth = userPlan.image_generations_used;
      if (usedThisMonth >= 1000) return 0.025;
      if (usedThisMonth >= 500) return 0.030;
      if (usedThisMonth >= 100) return 0.035;
      return 0.04;
    }
    return 1;
  };

  const getPlanLimits = () => {
    if (!userPlan) return { monthly: 0, remaining: 0, unlimited: false, usedThisMonth: 0 };
    
    return {
      monthly: userPlan.monthly_limit,
      remaining: userPlan.credits_remaining,
      unlimited: userPlan.monthly_limit === 999999,
      usedThisMonth: userPlan.image_generations_used
    };
  };

  const getBatchUploadLimit = () => {
    if (!userPlan) return 1;
    const plan = userPlan.plan_type;
    
    switch (plan) {
      case 'free': return 3;
      case 'hobby': return 10;
      case 'creator': return 50;
      case 'professional': return 999999;
      case 'business': return 999999;
      case 'pay_as_go': return 20;
      default: return 1;
    }
  };

  const getTeamMemberLimit = () => {
    if (!userPlan) return 0;
    const plan = userPlan.plan_type;
    
    switch (plan) {
      case 'creator': return 2;
      case 'professional': return 5;
      case 'business': return 999999;
      default: return 0;
    }
  };

  return {
    userPlan,
    loading,
    refetch: fetchUserPlan,
    getPlanDisplayName,
    getPlanBadgeColor,
    canAccessFeature,
    hasCreditsRemaining,
    getCreditCost,
    getPlanLimits,
    getBatchUploadLimit,
    getTeamMemberLimit
  };
};
